import React, { Component } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import {
    FlexContainer, CardList, LottieLoadingIndicator,
    Toast,
    BusinessPortalHeader,
    PAYMENT_FLOW_MAX_WIDTH,
    PortalCaption,
    PortalFormActions,
    PortalFormScrollBody,
    PortalFormSection,
    PortalFormSurface,
    PortalPanelHeader,
    PortalPaymentOptionGrid,
    PortalSectionTitle,
    StandardModalActionButton,
    StandardModalActionGroup
} from "src/components";

import Price from "../components/Price";
import SubscriptionOverview from "../components/SubscriptionOverview";
import "@trendmicro/react-radio/dist/react-radio.css";
import { SubscriptionContext } from "../SubscriptionContext";
import { Forms } from "../Subscription";
import AirwallexForm, { AirwallexProduct } from "src/scenes/Subscription/components/AirwallexForm";
import { calculateTotals } from "src/utils/helpers";
import { PaymentSelectionButton, PaymentSelectionIcon, Methods } from "./Review";


const Screens = {
    OVERVIEW: "Overview",
    PAYMENT: "Payment"
};

class Activate extends Component {
    state = {
        loading: false,
        method: Methods.EXISTING,
        screen: Screens.OVERVIEW,
        methodManuallyChanged: false
    };

    static contextType = SubscriptionContext;

    /**
     *  Returns a Promise that is being used to `await` in CardList.js thisPurchaseClicked()
    */
    purchaseInitialSubscription = (selectedMethod, methodSource) => {
        if (!selectedMethod || !methodSource) {
            return Toast.error("Please add a new card to continue.");
        }

        if (methodSource !== "airwallex") {
            console.log(`Bad method source: ${methodSource}`);
            return Toast.error("Only Airwallex payment methods are supported for activation. Please add a new card to continue.");
        }

        const { token, nextClick } = this.context;
        this.setState({
            loading: true
        });
        return axios.post(`${API_URL}/company/airwallex/purchaseNow/renewal`, {
            paymentMethodId: selectedMethod
        }, {
            headers: {
                Authorization: "Bearer " + token
            }
        }).then(() => {
            this.setState({
                loading: false
            });
            nextClick();
        }).catch(error => {
            this.setState({
                loading: false
            });
            return Toast.error(error.getApiErrorDetails().message);
        });
    };

    updateMethod = (value) => {
        this.setState({
            method: value,
            methodManuallyChanged: true
        });
    };

    handleCardsLoaded = (cards) => {
        const { methodManuallyChanged } = this.state;

        if (!methodManuallyChanged && (!cards || cards.length === 0)) {
            this.setState({ method: Methods.NEW });
        }
    };

    changeScreen = (screen) => {
        this.setState({ screen: screen });
    };

    navigateBack = () => {
        const { currentSubscription, backToPrevious } = this.context;
        const isPayingPreparedSubscription = Boolean(currentSubscription);
        if (!isPayingPreparedSubscription) {
            this.changeScreen(Screens.OVERVIEW);
            return;
        }
        backToPrevious();
    };

    ensureOverviewDefaults = () => {
        const { costing, updateValue, totalNumber, currencyCode } = this.context || {};
        const plan = costing && typeof costing.primaryPlan === "function" && costing.primaryPlan();
        if (!plan || typeof updateValue !== "function") return;

        if (!totalNumber || totalNumber < 5) updateValue("totalNumber", 5);
        if (!currencyCode && plan.currencyCode) updateValue("currencyCode", plan.currencyCode);
    };

    componentDidMount() {
        const { activateBusinessPayment, updateValue } = this.context || {};

        if (activateBusinessPayment) {
            if (this.changeScreen) {
                this.changeScreen(Screens.PAYMENT);
            } else {
                this.setState({ screen: Screens.PAYMENT });
            }
            updateValue?.("activateBusinessPayment", false);
        }

        this.ensureOverviewDefaults();
    }

    render() {
        //add method and updateValue when using radio buttons back
        const { totalNumber, companyName, paymentMode, backToPrevious, email,
            currentForm, token, nextClick, setUserPermissions, costing, currentSubscription } = this.context;

        const { method, screen } = this.state;

        const plan = (costing && typeof costing.primaryPlan === "function")
            ? costing.primaryPlan()
            : null;

        const hasPrimaryPlan = Boolean(plan);

        if (this.state.loading) {
            return <LottieLoadingIndicator />;
        }

        let currentFormIsCheckout = false;
        switch (currentForm) {
            case Forms.ACTIVATE_CHECKOUT:
                currentFormIsCheckout = true;
                break;
            default:
                console.error(`Incorrect form specified: ${currentForm}`);
                Toast.error("A form error occurred.");
        }

        const subTotalCents = currentSubscription.periodNetAmountCents
                     * totalNumber
                     * (currentSubscription?.periodsDue || 0);

        const { gst, processingFee, total: totalAmount } = calculateTotals({
            subtotalCents: subTotalCents,
            taxFraction: currentSubscription.taxFraction,
            processingRate: currentSubscription.processingFee
        });

        return (
            <React.Fragment>
                { screen === Screens.OVERVIEW ? (
                    hasPrimaryPlan ? (
                        <LottieLoadingIndicator />
                    ) : (
                        <SubscriptionOverview
                            totalNumber={Math.max(totalNumber || 0, 5)}
                            goToPayment={() => this.changeScreen(Screens.PAYMENT)}
                        />
                    ))
                    : (
                        <React.Fragment>
                            <BusinessPortalHeader
                                title="Complete Payment to Unlock Full Access"
                                description="To fully activate your Me Business account and access all features, please complete the payment for your user licenses. This will enable you to start inviting users to your custom Me Business app and utilise all available functionalities. Thank you for your prompt attention to this matter."
                                maxWidth={PAYMENT_FLOW_MAX_WIDTH}
                            />
                            <PortalFormSurface maxWidth={PAYMENT_FLOW_MAX_WIDTH} marginTop="0" flex="1 1 auto" minHeight="0">
                                <PortalFormScrollBody
                                    columns="repeat(2, minmax(0, 1fr))"
                                    alignItems="start"
                                    padding="1.25rem"
                                    gap="1rem"
                                >
                                    <PortalFormSection minHeight="max-content">
                                        <Price
                                            Forms={Forms}
                                            currentForm={Forms.REVIEW}
                                            companyName={companyName}
                                            totalNumber={Math.max(totalNumber || 0, 5)}
                                            periodsForPayment={currentSubscription?.periodsDue || 0}
                                            currentSubscription={currentSubscription}
                                            goToOverview={() => this.navigateBack()}
                                            processingFee={processingFee}
                                            gst={gst}
                                            totalAmount={totalAmount}
                                            hideBackButton
                                        />
                                    </PortalFormSection>
                                    <PortalFormSection minHeight="max-content">
                                        <PortalPanelHeader withDivider>
                                            <div>
                                                <PortalSectionTitle>Payment Method</PortalSectionTitle>
                                                <PortalCaption margin="0.25rem 0 0">
                                                    Add a card to complete activation.
                                                </PortalCaption>
                                            </div>
                                        </PortalPanelHeader>
                                        { currentFormIsCheckout &&
                                        <FlexContainer direction="column">
                                            <PortalPaymentOptionGrid>
                                                <PaymentSelectionButton
                                                    $active={method === Methods.NEW}
                                                    onClick={() => this.updateMethod(Methods.NEW)}
                                                >
                                                    <PaymentSelectionIcon $active={method === Methods.NEW} />
                                                    Add a new card
                                                </PaymentSelectionButton>
                                            </PortalPaymentOptionGrid>
                                            { this.state.method === Methods.NEW ?
                                                <FlexContainer align="start" direction="column" flexGrow="1">
                                                    <AirwallexForm
                                                        token={token} email={email}
                                                        productType={AirwallexProduct.INITIAL}
                                                        companyName={companyName}
                                                        numberOfUsers={totalNumber}
                                                        paymentMode={paymentMode}
                                                        setUserPermissions={setUserPermissions}
                                                        backToPrevious={backToPrevious}
                                                        nextClick={nextClick}
                                                    />
                                                </FlexContainer>
                                                :
                                                <CardList
                                                    token={token}
                                                    onBackClicked={() => backToPrevious()}
                                                    onPurchaseClicked={this.purchaseInitialSubscription}
                                                    onDeleteClicked={null}
                                                    onCardsLoaded={this.handleCardsLoaded}
                                                    supportedSources={["airwallex"]}
                                                    hideBackButton
                                                    minHeight="auto"
                                                />
                                            }
                                        </FlexContainer>
                                        }
                                    </PortalFormSection>
                                </PortalFormScrollBody>
                                <PortalFormActions sticky={false}>
                                    <StandardModalActionGroup $pushRight>
                                        <StandardModalActionButton type="button" onClick={() => this.navigateBack()}>
                                            Back
                                        </StandardModalActionButton>
                                    </StandardModalActionGroup>
                                </PortalFormActions>
                            </PortalFormSurface>
                        </React.Fragment>
                    )}
            </React.Fragment>
        );
    }
}

export default Activate;
